import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Grid, Paper } from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import { useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

// styles
import useStyles from "./styles";

// components
import MovieDescription from "../../components/MovieDescription";
import MovieAlbum from "../../components/MovieAlbum";
import MovieMenu from "../../components/MovieMenu";
import VideoSideBar from "../../components/VideoSideBar";

// context
import { useMatomo } from "@datapunt/matomo-tracker-react";
import { useVideoState } from "../../context/VideoContext";

import { getVideoInfo } from "../../clientAPI";
import tracker from "../../utils/tracker";
import { getTabID } from "../../utils/common";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import slice from "lodash/slice";

import VideoJsPlayer from "../../components/VideoJsPlayer";
import { TrendingUpRounded } from "@material-ui/icons";
import { getVideoUrl } from "../../utils/common";

const MovieDetail = props => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const tabId = getTabID();
  const { trackEvent, pushInstruction } = useMatomo();
  const { playId, trackIndex = 0 } = useVideoState();
  const { videoId } = useParams();
  const videoType =
    window.location.pathname.indexOf("trailer") !== -1 ? "trailer" : "movie";

  const [videoInfoState, setVideoInfoState] = useState({});

  const videoUrl = getVideoUrl(videoInfoState, videoType, trackIndex);
  // below 960px
  const downSm = useMediaQuery(theme.breakpoints.down("sm"), {
    defaultMatches: false,
  });

  // TODO: check whether add recommended videos
  // TODO: expand all different lang videos
  const trailerItems = get(videoInfoState, `play.trailer`, [])
    .slice(0, 1)
    .map(e => ({
      ...e,
      videoId: videoId,
      title: "預告片",
      videoType: "trailer",
      langName: e.langName,
      thumbnail:
        e.url.split("/manifest")[0] +
        "/thumbnails/thumbnail.jpg?time=5s&width=160&height=90",
    }));
  const movieItems = get(videoInfoState, `play.movie`, [])
    .slice(0, 1)
    .map(e => ({
      ...e,
      videoId: videoId,
      title: "正片",
      videoType: "movie",
      langName: e.langName,
      url: e.url,
      thumbnail: get(trailerItems, "0.thumbnail", "").replace(
        "time=5s",
        "time=2s",
      ),
      videoUrl: getVideoUrl(videoInfoState, videoType, trackIndex),
    }));
  const sideBarItems = [].concat(movieItems, trailerItems);

  const playerType = "videojs";

  const videoJsOptions = {
    aspectRatio: "16:9",
    preload: "auto",
    autoplay: false,
    controls: true,
    responsive: true,
    bigPlayButton: TrendingUpRounded,
    errorDisplay: false,
    controlBar: { pictureInPictureToggle: false },
    sources: [
      {
        src: videoUrl,
      },
    ],
    playbackRates: [2, 1.5, 1.25, 1, 0.75, 0.5],
    language: "zh-TW",
    html5: {
      // fine tuning: https://github.com/video-dev/hls.js/blob/master/docs/API.md#fine-tuning
      vhs: {
        maxBufferLength: 30,
        maxMaxBufferLength: 30,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,

        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.1,
        nudgeMaxRetry: 3,
        maxFragLookUpTolerance: 0.25,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: Infinity,
        liveDurationInfinity: false,
        enableWorker: true,
        enableSoftwareAES: true,

        manifestLoadingTimeOut: 3000,
        manifestLoadingMaxRetry: 20,
        manifestLoadingRetryDelay: 3000,
        manifestLoadingMaxRetryTimeout: 10000,

        startLevel: undefined,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 4,
        levelLoadingRetryDelay: 1000,
        levelLoadingMaxRetryTimeout: 64000,
        fragLoadingTimeOut: 3000,
        fragLoadingMaxRetry: 6,
        fragLoadingRetryDelay: 1000,
        fragLoadingMaxRetryTimeout: 20000,
        startFragPrefetch: false,
        testBandwidth: true,
        progressive: false,
        lowLatencyMode: true,
        fpsDroppedMonitoringPeriod: 5000,
        fpsDroppedMonitoringThreshold: 0.2,
        appendErrorMaxRetry: 5,

        enableWebVTT: true,
        enableIMSC1: true,
        enableCEA708Captions: true,
        stretchShortVideoTrack: false,
        maxAudioFramesDrift: 1,
        forceKeyFrameOnDiscontinuity: true,
        abrEwmaFastLive: 3.0,
        abrEwmaSlowLive: 9.0,
        abrEwmaFastVoD: 3.0,
        abrEwmaSlowVoD: 9.0,
        abrEwmaDefaultEstimate: 500000,
        abrBandWidthFactor: 0.95,
        abrBandWidthUpFactor: 0.7,
        abrMaxWithRealBitrate: false,
        maxStarvationDelay: 4,
        maxLoadingDelay: 4,
        minAutoBitrate: 0,
        emeEnabled: false,
        widevineLicenseUrl: undefined,
        licenseXhrSetup: undefined,
        drmSystemOptions: {},
      },
    },
  };

  const playerRef = useRef();

  useEffect(() => {
    if (!videoId) return;
    getVideoInfo(videoId)
      .then(data => {
        setVideoInfoState(state => ({ ...state, ...data }));
      })
      .catch(err => {
        console.log(err);
        history.push("/error");
      });
  }, [videoId]);

  useEffect(() => {
    if (!videoId || !videoType) return;

    tracker.viewedVideoPage({
      trackEvent,
      pushInstruction,
      videoType,
      videoId,
      tabId,
    });
  }, [videoId, videoType]);

  const isIE =
    navigator.userAgent.indexOf("MSIE") !== -1 ||
    navigator.appVersion.indexOf("Trident/") > -1;

  const ErrorMask = ({ content }) => (
    <div className={classes.geoContainer}>
      <div className={classes.textCenter}>
        <div>{content}，</div>
        <div>造成不便敬請見諒。</div>
      </div>
    </div>
  );

  const VideoJsContainer = ({ url, videoInfoState }) => {
    if (isEmpty(url) && videoInfoState && videoInfoState.ipRestricted) {
      return <ErrorMask content='您目前所在的位置或使用的帳號無法觀看此影片' />;
    }
    if (isEmpty(url) && videoInfoState && videoInfoState.geoRestricted) {
      return <ErrorMask content='目前這部影片無法在您所在的區域播放' />;
    }
    if (
      videoType === "movie" &&
      videoInfoState &&
      videoInfoState.subscribedRestricted
    ) {
      return <ErrorMask content='目前這部影片無法播放因使用者授權已到期' />;
    }
    if (isEmpty(url) && videoType === "movie") {
      return <ErrorMask content='目前這部影片無法播放' />;
    }
    if (isEmpty(url) && videoType === "trailer") {
      return <ErrorMask content='此影片無預告片請直接點擊播放正片' />;
    }

    return (
      <VideoJsPlayer
        forwardedRef={playerRef}
        options={videoJsOptions}
        videoInfoState={videoInfoState}
      />
    );
  };

  function areEqual (prevProps, nextProps) {
    /*
    return true if passing nextProps to render would return
    the same result as passing prevProps to render,
    otherwise return false
    */
    return (
      prevProps.url === nextProps.url &&
      prevProps.videoInfoState &&
      prevProps.videoInfoState.id === nextProps.videoInfoState.id &&
      prevProps.isXs === nextProps.isXs
    );
  }

  function videoJsAreEqual (prevProps, nextProps) {
    /*
    return true if passing nextProps to render would return
    the same result as passing prevProps to render,
    otherwise return false
    */
    return (
      prevProps.url === nextProps.url &&
      prevProps.videoInfoState &&
      prevProps.videoInfoState.id === nextProps.videoInfoState.id
    );
  }

  const MemorizedVideoJsContainer = React.memo(
    VideoJsContainer,
    videoJsAreEqual,
  );
  return (
    <>
      {!isEmpty(videoInfoState) && (
        <div className={classes.movieDetailContainer} key={playId}>
          <Grid
            container
            spacing={4}
            direction='row'
            alignItems='center'
            justify='center'
          >
            <Grid item xs={12}>
              <div>
                <MovieMenu />
              </div>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.playerContainer}>
                {downSm ? (
                  <div className={classes.player}>
                    <MemorizedVideoJsContainer
                      url={videoUrl}
                      videoInfoState={videoInfoState}
                    />
                  </div>
                ) : (
                  <Grid container direction='row'>
                    <Grid item xs={9}>
                      <div className={classes.player}>
                        <MemorizedVideoJsContainer
                          url={videoUrl}
                          videoInfoState={videoInfoState}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={3}>
                      <div>
                        <VideoSideBar
                          playerRef={playerRef}
                          items={sideBarItems}
                        />
                      </div>
                    </Grid>
                  </Grid>
                )}
              </div>
            </Grid>
            <Grid item xs={12}>
              <Paper className={classes.movieDescriptionContainer}>
                <MovieDescription
                  playerRef={playerRef}
                  videoId={videoId}
                  videoInfo={videoInfoState}
                  videoType={videoType}
                  trackIndex={trackIndex}
                />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.recommend}>
                <MovieAlbum
                  slot={videoInfoState.recommends}
                  hoverMaskMode='simple'
                />
              </div>
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};

MovieDetail.defaultProps = {};

MovieDetail.propsTypes = {};

export default MovieDetail;
